<template>
  <div class="weekly-view-pending-reschedule w-full max-w-full min-w-0 bg-white border rounded-lg overflow-hidden" style="border-color: #5b80be;">
    <div
      class="flex flex-wrap items-center justify-between gap-2 py-2 px-3 text-sm border-b"
      style="background-color: #eef3fb; border-color: #c5d4eb;"
    >
      <div class="flex items-center gap-2 font-semibold" style="color: #3b66bc;">
        <b-icon pack="fas" icon="thumbtack" size="is-small" />
        <span
          class="inline-flex items-center py-px px-1.5 rounded-sm text-white font-bold uppercase"
          style="background-color: #3b66bc; font-size: 10px;"
        >{{ $t('weekly_pending_reschedule') }}</span>
        <span>{{ summaryLabel }}</span>
      </div>
      <button
        type="button"
        class="weekly-view-pending-reschedule__back-btn"
        @click="$emit('close')"
      >
        <b-icon pack="fas" icon="arrow-left" size="is-small" />
        <span>{{ $t('back') }}</span>
      </button>
    </div>

    <div v-if="isLoading" class="py-8 text-center text-sm text-gray-500">
      {{ $t('loading') }}...
    </div>

    <template v-else>
      <div class="weekly-view-pending-reschedule__scroll relative w-full max-w-full min-w-0 overflow-x-auto">
        <table class="weekly-view-pending-reschedule__table w-full text-xs border-collapse min-w-max">
          <thead>
            <tr>
              <th
                v-for="column in columns"
                :key="column.key"
                class="weekly-view-pending-reschedule__th"
                :class="{ 'weekly-view-pending-reschedule__th--left': column.align === 'left' }"
              >
                {{ $t(column.labelKey) }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!rows.length">
              <td :colspan="columns.length" class="weekly-view-pending-reschedule__empty">
                {{ $t('no_results') }}
              </td>
            </tr>
            <tr
              v-for="row in rows"
              :key="row.id"
              class="weekly-view-pending-reschedule__row"
            >
              <td class="weekly-view-pending-reschedule__td text-center whitespace-nowrap">
                <span class="weekly-view-pending-reschedule__week-badge">{{ row.weekLabel }}</span>
              </td>
              <td class="weekly-view-pending-reschedule__td text-center whitespace-nowrap">
                <span class="weekly-view-pending-reschedule__id-badge">{{ row.parentIdLabel }}</span>
              </td>
              <td class="weekly-view-pending-reschedule__td weekly-view-pending-reschedule__td--name">
                {{ row.parentName || '—' }}
              </td>
              <td class="weekly-view-pending-reschedule__td weekly-view-pending-reschedule__td--name">
                {{ row.subactivityName || '—' }}
              </td>
              <td class="weekly-view-pending-reschedule__td text-center">
                <span
                  v-if="row.disciplineName"
                  class="weekly-view-pending-reschedule__discipline-badge"
                  :style="{ backgroundColor: row.disciplineColor }"
                >
                  {{ row.disciplineName }}
                </span>
                <span v-else>—</span>
              </td>
              <td class="weekly-view-pending-reschedule__td whitespace-nowrap">
                {{ row.manager || '—' }}
              </td>
              <td class="weekly-view-pending-reschedule__td text-center whitespace-nowrap">
                <span v-if="row.progress != null">{{ formatProgress(row.progress) }}</span>
                <span v-else>—</span>
              </td>
              <td class="weekly-view-pending-reschedule__td text-center whitespace-nowrap">
                <button
                  type="button"
                  class="weekly-view-pending-reschedule__action-btn"
                  @click="openEditModal(row)"
                >
                  <b-icon pack="fas" icon="calendar-alt" size="is-small" />
                  <span>{{ rescheduleActionLabel }}</span>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        v-if="total > perPage"
        class="flex flex-wrap items-center justify-between gap-2 py-2 px-3 border-t"
        style="border-color: #c5d4eb;"
      >
        <span class="text-xs text-gray-600">{{ $t('count_results') }}: {{ total }}</span>
        <b-pagination
          :total="total"
          :current="page"
          :per-page="perPage"
          size="is-small"
          order="is-centered"
          pack="fas"
          icon-pack="fas"
          icon="chevron-left"
          icon-right="chevron-right"
          @change="onPageChange"
        />
      </div>
    </template>

    <weekly-edit-activity-modal
      ref="editActivityModal"
      :project-id="effectiveProjectId"
      :reschedule-mode="rescheduleMode"
      @saved="onActivitySaved"
    />
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import { mapGetters } from 'vuex'
import { buildProjectWeeks, findWeekIndexForDate } from './weeklyProjectWeeks.js'
import WeeklyEditActivityModal from './weekly-edit-activity-modal.vue'

const ACTIVITY_STATUS_PENDING_RESCHEDULING = 9
const DEFAULT_DISCIPLINE_COLOR = '#ea580c'

const TABLE_COLUMNS = [
  { key: 'week', labelKey: 'week', align: 'center' },
  { key: 'activity_id', labelKey: 'weekly_table_activity_id', align: 'center' },
  { key: 'activity', labelKey: 'activity', align: 'left' },
  { key: 'subactivity', labelKey: 'subactivity', align: 'left' },
  { key: 'discipline', labelKey: 'discipline', align: 'center' },
  { key: 'manager', labelKey: 'manager', align: 'left' },
  { key: 'progress', labelKey: 'progress', align: 'center' },
  { key: 'action', labelKey: 'action', align: 'center' },
]

function buildManagerLabel(manager) {
  if (!manager) {
    return null
  }
  if (manager.fullname) {
    return manager.fullname
  }
  return `${manager.name || ''} ${manager.surname || ''}`.trim() || null
}

function resolveGroupColor(group) {
  if (!group) {
    return DEFAULT_DISCIPLINE_COLOR
  }
  if (group.color) {
    return group.color
  }
  if (group.configs) {
    try {
      const configs = typeof group.configs === 'string' ? JSON.parse(group.configs) : group.configs
      if (configs && configs.color) {
        return configs.color
      }
    } catch (error) {
      return DEFAULT_DISCIPLINE_COLOR
    }
  }
  return DEFAULT_DISCIPLINE_COLOR
}

function parentActivityIdLabel(parent) {
  if (!parent) {
    return '—'
  }
  if (parent.planning_code) {
    return parent.planning_code
  }
  if (parent.id != null) {
    return `ACT-${String(parent.id).padStart(3, '0')}`
  }
  return '—'
}

function resolveWeekLabel(projectWeeks, startDate) {
  if (!projectWeeks.length || !startDate) {
    return '—'
  }
  const parsed = moment(startDate, ['DD/MM/YYYY', 'YYYY-MM-DD'], true)
  if (!parsed.isValid()) {
    return '—'
  }
  const index = findWeekIndexForDate(projectWeeks, parsed)
  const week = projectWeeks[index]
  if (!week) {
    return '—'
  }
  return `S${week.weekNumber}`
}

function mapPendingRow(item, options) {
  const { isSubactivityMode, projectWeeks } = options
  const parent = isSubactivityMode ? (item.parent || {}) : item
  const manager = item.manager || parent.manager || null
  const group = item.group || parent.group || null
  const startDate = item.current_start_date || null

  return {
    id: item.id,
    weekLabel: resolveWeekLabel(projectWeeks, startDate),
    weekStartDate: startDate,
    parentIdLabel: parentActivityIdLabel(parent),
    parentActivityId: parent.id != null ? parent.id : null,
    parentName: isSubactivityMode ? (parent.name || null) : (item.name || null),
    subactivityName: isSubactivityMode ? (item.name || null) : null,
    disciplineName: group && group.name ? group.name : null,
    disciplineColor: resolveGroupColor(group),
    manager: buildManagerLabel(manager),
    progress: item.progress != null ? item.progress : null,
    current_start_date: startDate,
    current_end_date: item.current_end_date || null,
    is_subactivity: isSubactivityMode,
  }
}

export default {
  name: 'weekly-view-pending-reschedule',

  components: {
    WeeklyEditActivityModal,
  },

  props: {
    projectId: {
      type: Number,
      default: null,
    },
    weekNumber: {
      type: Number,
      default: null,
    },
    targetWeekLabel: {
      type: String,
      default: '',
    },
    isSubactivityMode: {
      type: Boolean,
      default: false,
    },
    rescheduleMode: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isLoading: false,
      rows: [],
      page: 1,
      perPage: 25,
      total: 0,
      columns: TABLE_COLUMNS,
    }
  },

  computed: {
    ...mapGetters({
      project: 'app/project',
    }),

    effectiveProjectId() {
      return this.projectId || (this.project && this.project.id) || null
    },

    projectWeeks() {
      if (!this.project) {
        return []
      }
      return buildProjectWeeks(
        this.project.current_start_date,
        this.project.current_end_date
      )
    },

    summaryLabel() {
      return this.$t('weekly_pending_reschedule_summary', { count: this.total })
    },

    rescheduleActionLabel() {
      if (this.targetWeekLabel) {
        return this.$t('weekly_reschedule_to', { week: this.targetWeekLabel })
      }
      return this.$t('reschedule')
    },
  },

  watch: {
    effectiveProjectId(projectId, previousProjectId) {
      if (projectId && projectId !== previousProjectId) {
        this.loadRows(1)
      }
    },
    isSubactivityMode() {
      this.loadRows(1)
    },
  },

  created() {
    this.loadRows(1)
  },

  methods: {
    buildRequestParams(page) {
      const projectId = this.effectiveProjectId
      if (!projectId) {
        return null
      }

      const params = {
        p: projectId,
        status: ACTIVITY_STATUS_PENDING_RESCHEDULING,
        order_field: 'planning_code',
        order_dir: 'asc',
        per_page: this.perPage,
        page,
      }

      if (this.isSubactivityMode) {
        params.filter_subactivities = 1
        params.include_parents_for_accessible_children = 1
      } else {
        params.pg = 1
        delete params.per_page
        delete params.page
      }

      return params
    },

    async loadRows(page = 1) {
      const params = this.buildRequestParams(page)
      if (!params) {
        this.rows = []
        this.total = 0
        this.isLoading = false
        return
      }

      this.isLoading = true
      this.page = page

      try {
        const { data } = await axios.get('/api/v2/activities', { params })

        if (!data || !data.success || !Array.isArray(data.activities)) {
          this.rows = []
          this.total = 0
          this.$notify.error('error_loading_activities')
          return
        }

        const mapOptions = {
          isSubactivityMode: this.isSubactivityMode,
          projectWeeks: this.projectWeeks,
        }
        this.rows = data.activities.map((item) => mapPendingRow(item, mapOptions))
        this.total = data.total != null ? data.total : this.rows.length
        this.$emit('loaded', { total: this.total })
      } catch (error) {
        this.rows = []
        this.total = 0
        this.$notify.error('error_loading_activities')
      } finally {
        this.isLoading = false
      }
    },

    onPageChange(nextPage) {
      this.loadRows(nextPage)
    },

    formatProgress(value) {
      const number = Number(value)
      if (Number.isNaN(number)) {
        return `${value}%`
      }
      return `${Math.round(number)}%`
    },

    refresh() {
      this.loadRows(this.page)
    },

    openEditModal(row) {
      if (!row || row.id == null || !this.$refs.editActivityModal) {
        return
      }
      this.$refs.editActivityModal.show({
        row,
        isSubactivity: !!row.is_subactivity,
      })
    },

    onActivitySaved() {
      this.refresh()
    },
  },
}
</script>

<style scoped>
.weekly-view-pending-reschedule__back-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.625rem;
  border: 1px solid #c5d4eb;
  border-radius: 0.25rem;
  background: #fff;
  color: #3b66bc;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
}

.weekly-view-pending-reschedule__back-btn:hover {
  background: #eef3fb;
}

.weekly-view-pending-reschedule__th {
  padding: 0.5rem 0.625rem;
  border: 1px solid #4a6fa5;
  background: #3b66bc;
  color: #fff;
  font-weight: 700;
  text-transform: uppercase;
  text-align: center;
  white-space: nowrap;
}

.weekly-view-pending-reschedule__th--left {
  text-align: left;
}

.weekly-view-pending-reschedule__td {
  padding: 0.5rem 0.625rem;
  border: 1px solid #dbe6f5;
  color: #1f2937;
  vertical-align: middle;
  background: #fff;
}

.weekly-view-pending-reschedule__td--name {
  max-width: 16rem;
}

.weekly-view-pending-reschedule__row:hover .weekly-view-pending-reschedule__td {
  background: #f8fafc;
}

.weekly-view-pending-reschedule__week-badge,
.weekly-view-pending-reschedule__id-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2.5rem;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  background: #eef3fb;
  color: #3b66bc;
  font-size: 0.625rem;
  font-weight: 700;
}

.weekly-view-pending-reschedule__discipline-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
  color: #fff;
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  white-space: nowrap;
}

.weekly-view-pending-reschedule__action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border: 1px solid #3b66bc;
  border-radius: 0.25rem;
  background: #fff;
  color: #3b66bc;
  font-size: 0.6875rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.weekly-view-pending-reschedule__action-btn:hover {
  background: #eef3fb;
}

.weekly-view-pending-reschedule__empty {
  padding: 1.5rem;
  text-align: center;
  color: #6b7280;
}
</style>
